import type { Curve } from './data';

/**
 * What the viewer draws and in what order. The layout holds only references to
 * curves — by key — and how to draw them, so it is small enough to keep per
 * well in the browser and survives the data being reloaded.
 */

export interface TrackCurve {
  key: string;
  color: string;
  min: number;
  max: number;
  log: boolean;
  /**
   * A continuous line, a marker at each sample, the two together, or a marker
   * and its value at each sample — the way the composite log draws survey
   * stations, which are tens of metres apart.
   */
  style?: CurveStyle;
  /** The line's pattern; solid when absent. */
  dash?: Dash;
  /** Line width in pixels; 1.2 when absent. */
  width?: number;
  /** The marker for symbols; a circle when absent. */
  symbol?: SymbolShape;
}

export type CurveStyle = 'line' | 'symbols' | 'line+symbols' | 'stations';
export type Dash = 'solid' | 'dashed' | 'dotted' | 'dash-dot';
export type SymbolShape = 'circle' | 'square' | 'triangle' | 'diamond' | 'cross' | 'plus';

export const CURVE_STYLES: { value: CurveStyle; label: string }[] = [
  { value: 'line', label: 'Line' },
  { value: 'symbols', label: 'Symbols' },
  { value: 'line+symbols', label: 'Line + symbols' },
  { value: 'stations', label: 'Survey stations' },
];

export const DASHES: { value: Dash; label: string }[] = [
  { value: 'solid', label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
  { value: 'dash-dot', label: 'Dash-dot' },
];

export const LINE_WIDTHS: { value: number; label: string }[] = [
  { value: 0.8, label: 'Thin' },
  { value: 1.2, label: 'Normal' },
  { value: 2.2, label: 'Thick' },
];

export const SYMBOLS: { value: SymbolShape; label: string }[] = [
  { value: 'circle', label: '● Circle' },
  { value: 'square', label: '■ Square' },
  { value: 'triangle', label: '▲ Triangle' },
  { value: 'diamond', label: '◆ Diamond' },
  { value: 'cross', label: '✕ Cross' },
  { value: 'plus', label: '+ Plus' },
];

/**
 * The on-off lengths of a pattern, grown with the line so a thick dashed line
 * still reads as dashed. Dotted draws near-zero dashes with round caps.
 */
export function dashArray(dash: Dash | undefined, width: number): number[] {
  const k = Math.max(1, width);
  switch (dash) {
    case 'dashed':
      return [6 * k, 4 * k];
    case 'dotted':
      return [0.01, 3 * k];
    case 'dash-dot':
      return [7 * k, 3 * k, 0.01, 3 * k];
    default:
      return [];
  }
}

/** Survey stations are sparse points, not a log: they draw as markers. */
export function isStationCurve(curve: Curve) {
  return /^incl/i.test(curve.mnemonic) || (/survey/i.test(curve.source) && /deg/i.test(curve.unit));
}

export type Track =
  /**
   * Depth labels. Without a curve they are the measured depth the log runs
   * on; with one, that curve's value at each line, e.g. TVD as the
   * alternative depth beside the primary one.
   */
  | { id: string; kind: 'depth'; width: number; curve?: string }
  | { id: string; kind: 'lithology'; width: number }
  | { id: string; kind: 'curves'; width: number; title: string; curves: TrackCurve[] };

export const PALETTE = ['#2e7d32', '#c62828', '#1565c0', '#6a1b9a', '#ef6c00', '#00838f', '#5d4037', '#ad1457'];

/** C1 to nC5, lightest to heaviest. */
const CHROMATOGRAPH_COLOURS = ['#c62828', '#ef6c00', '#2e7d32', '#1565c0', '#6a1b9a', '#00838f', '#5d4037'];

/** Patterns as on the printed log, so the gases part even where colours are close. */
const CHROMATOGRAPH_DASHES: Dash[] = ['solid', 'dotted', 'solid', 'dashed', 'dotted', 'dashed', 'dash-dot'];

export const TRACK_WIDTHS = { narrow: 110, normal: 170, wide: 260 } as const;

/**
 * A depth track is narrow by default, its labels written up the column as on
 * the printed log, so the depth hugs the left edge and the log fits the page.
 * Wide writes them across, for anyone who would rather read them that way.
 */
export const DEPTH_WIDTHS = { narrow: 30, wide: 70 } as const;

/** Narrow enough that depth labels are turned on their side. */
export function isVerticalDepth(width: number) {
  return width < 50;
}

/** Vertical scales as a geologist says them, and the pixels per metre each means on screen. */
export const SCALES = [
  { label: '1:200', pxPerM: 18.9 },
  { label: '1:500', pxPerM: 7.56 },
  { label: '1:1000', pxPerM: 3.78 },
  { label: '1:2000', pxPerM: 1.89 },
  { label: '1:5000', pxPerM: 0.756 },
] as const;

let counter = 0;
export function newId() {
  counter += 1;
  return `t${Date.now().toString(36)}${counter}`;
}

/** Resistivity reads on a log scale; everything else starts linear. */
export function isLogUnit(unit: string) {
  return /ohm/i.test(unit);
}

/** A sensible starting scale for a curve: its own range, or the usual one for its kind. */
export function defaultScale(curve: Curve): { min: number; max: number; log: boolean } {
  // Always the standard 0.2–2000: every resistivity in a track then shares one
  // scale and can be read against the others, and a spike cannot stretch it.
  if (isLogUnit(curve.unit)) return { min: 0.2, max: 2000, log: true };
  // Inclination: a few degrees on a vertical well, so 0 to the next whole 5.
  if (/^incl/i.test(curve.mnemonic)) {
    return { min: 0, max: Math.max(5, Math.ceil(curve.max / 5) * 5), log: false };
  }
  // Chromatograph gases span four decades, 10 to 100 000 ppm, as on the composite log.
  if (/^ppm$/i.test(curve.unit)) return { min: 10, max: 100000, log: true };
  // Total gas reads linear, 0–10 %, widened by tens when a show runs past it.
  if (/TGAS|total\s*gas/i.test(curve.mnemonic)) {
    return { min: 0, max: Math.max(10, Math.ceil(curve.max / 10) * 10), log: false };
  }
  // The usual 0–150 API, stretched in steps of 50 when the well runs hotter.
  if (/^api$/i.test(curve.unit) || /GR/i.test(curve.mnemonic)) {
    return { min: 0, max: Math.max(150, Math.ceil(curve.max / 50) * 50), log: false };
  }

  const span = curve.max - curve.min || 1;
  const round = (v: number) => Number(v.toPrecision(2));
  return { min: round(Math.min(0, curve.min)), max: round(curve.max + span * 0.05), log: false };
}

export function trackCurve(curve: Curve, color: string): TrackCurve {
  return {
    key: curve.key,
    color,
    ...defaultScale(curve),
    style: isStationCurve(curve) ? 'stations' : 'line',
  };
}

/**
 * Which starting layout, and where its changes are kept. The Log Viewer in
 * Documents and the composite log in RT Logs read the same files but are
 * arranged separately, so rearranging one leaves the other as it was.
 */
export type Preset = 'viewer' | 'composite' | 'rt-depth' | 'pressure' | 'pore-pressure';

/** The real-time logs read from the bit up; the viewer and the pressure log read down. */
export const STARTS_FLIPPED: Record<Preset, boolean> = {
  viewer: false,
  composite: true,
  'rt-depth': true,
  pressure: false,
  'pore-pressure': true,
};

type Part =
  | 'depth'
  | 'alt-depth'
  | 'gamma+rop'
  | 'inclination'
  | 'lithology'
  | 'resistivity'
  | 'gas'
  | 'chromatograph'
  | 'composite-gamma'
  | 'composite-resistivity'
  | 'composite-gas'
  | 'drilling'
  | 'mud-weight';

/** A column the printed log has and the data files do not fill yet: drawn empty, titled, ready for a curve. */
type Blank = { blank: string; width: number };

const ORDER: Record<Preset, (Part | Blank)[]> = {
  viewer: ['depth', 'gamma+rop', 'inclination', 'lithology', 'resistivity', 'gas', 'chromatograph'],
  // Left to right as on the printed composite log, with its scales. Every
  // column is there even with nothing to draw in it, so the log reads like
  // the printed one and a curve can be added as soon as there is data.
  composite: [
    'depth',
    'composite-gamma',
    'alt-depth',
    'inclination',
    'lithology',
    { blank: 'Operation Remarks', width: 160 },
    { blank: 'Shows', width: 60 },
    'composite-resistivity',
    { blank: 'Density & Neutron', width: 170 },
    { blank: 'Sonic', width: 170 },
    'composite-gas',
    'chromatograph',
    { blank: 'Calcimetry', width: 70 },
    { blank: '% Litho', width: 70 },
    { blank: 'Descriptions', width: 260 },
    { blank: 'Depositional Environment', width: 60 },
  ],
  // What the driller watches while drilling ahead: the formation curves and
  // gas against the parameters at the bit.
  'rt-depth': [
    'depth',
    'composite-gamma',
    'composite-resistivity',
    'composite-gas',
    'chromatograph',
    'drilling',
    'lithology',
  ],
  // The printed pressure log's columns; the pressure ones fill once there is
  // pretest and pore pressure data to put in them.
  pressure: [
    'depth',
    { blank: 'Pretest', width: 60 },
    'lithology',
    'composite-gamma',
    'composite-resistivity',
    { blank: 'Porosity', width: 170 },
    'composite-gas',
    'chromatograph',
    { blank: 'Haworth', width: 60 },
    { blank: 'Pixler', width: 60 },
    { blank: 'Oil Shaking', width: 60 },
    'alt-depth',
    { blank: 'Pore Pressure', width: 420 },
  ],
  // Pore pressure and fracture gradient against the mud weight in the hole;
  // the gradients fill once a pore pressure model is uploaded.
  'pore-pressure': [
    'depth',
    'alt-depth',
    'lithology',
    'composite-gamma',
    { blank: 'Pore Pressure / Fracture Gradient', width: 460 },
    'mud-weight',
  ],
};

/**
 * One track of the starting layout, or null when the well has no curve for it.
 * With keepEmpty the track stays, titled and empty, as the composite wants.
 */
function part(name: Part, curves: Curve[], hasLithology: boolean, keepEmpty = false): Track | null {
  const find = (...patterns: RegExp[]) =>
    patterns.map((p) => curves.find((c) => p.test(c.mnemonic))).find(Boolean);
  const findAll = (pattern: RegExp, limit: number) =>
    curves.filter((c) => pattern.test(c.mnemonic)).slice(0, limit);
  const track = (title: string, width: number, list: (TrackCurve | undefined)[]): Track | null => {
    const present = list.filter((c): c is TrackCurve => Boolean(c));
    return present.length || keepEmpty ? { id: newId(), kind: 'curves', width, title, curves: present } : null;
  };

  const gr = find(/^DGRCC$/i, /GR/i);
  const rop = find(/^ROPA$/i, /^ROP/i);

  switch (name) {
    case 'depth':
      return { id: newId(), kind: 'depth', width: DEPTH_WIDTHS.narrow };
    case 'alt-depth': {
      // Several files carry TVD; the one that starts shallowest labels the most of the log.
      const tvd = curves
        .filter((c) => /^(DEP)?TVD$/i.test(c.mnemonic))
        .sort((a, b) => a.depth[0] - b.depth[0])[0];
      return tvd
        ? { id: newId(), kind: 'depth', width: DEPTH_WIDTHS.narrow, curve: tvd.key }
        : track('Alt. Depth', DEPTH_WIDTHS.narrow, []);
    }
    case 'lithology':
      if (hasLithology) return { id: newId(), kind: 'lithology', width: 80 };
      return track('Int. Litho', 80, []);
    case 'gamma+rop':
      return track('Gamma Ray', TRACK_WIDTHS.normal, [
        gr && trackCurve(gr, PALETTE[0]),
        rop && trackCurve(rop, PALETTE[5]),
      ]);
    // ROP reads right to left, 500 to 0, so a fast section swings out to the left.
    case 'composite-gamma': {
      const cali = find(/^CAL/i);
      return track('Gamma Ray, Cali & ROP', TRACK_WIDTHS.normal, [
        rop && { ...trackCurve(rop, '#2e7d32'), min: 500, max: 0, log: false },
        gr && { ...trackCurve(gr, '#2e7d32'), min: 0, max: 200, log: false },
        cali && { ...trackCurve(cali, '#24313a'), min: 6, max: 22, log: false, dash: 'dotted' },
      ]);
    }
    case 'inclination': {
      const incl = find(/^incl/i);
      return track('Inclination', TRACK_WIDTHS.narrow, [incl && trackCurve(incl, '#d32f2f')]);
    }
    case 'resistivity':
      return track(
        'Resistivity',
        TRACK_WIDTHS.normal,
        findAll(/^R\d+PC$/i, 3).map((c, i) => trackCurve(c, PALETTE[[1, 2, 3][i]])),
      );
    // Total gas and the chromatograph are separate tracks, as on the composite
    // log: one is linear and the other logarithmic, and a track has one grid.
    // Shallow and deep reading, 0.2 to 200 as printed. The same mnemonic can
    // come from more than one file; the first one found is used.
    case 'composite-resistivity': {
      const shallow = find(/^R15PC$/i, /^R09PC$/i);
      const deep = find(/^R39PC$/i, /^R27PC$/i);
      const scale = { min: 0.2, max: 200, log: true };
      return track('Resistivity', TRACK_WIDTHS.normal, [
        shallow && { ...trackCurve(shallow, '#c62828'), ...scale },
        deep && { ...trackCurve(deep, '#1565c0'), ...scale, dash: 'dashed' },
      ]);
    }
    case 'composite-gas': {
      const gas = find(/^TGAS/i);
      return track('TG', TRACK_WIDTHS.narrow, [gas && { ...trackCurve(gas, '#d32f2f'), min: 0, max: 10, log: false }]);
    }
    case 'drilling':
      return track(
        'Drilling',
        TRACK_WIDTHS.normal,
        [/^WOB/i, /^TORQ/i, /^SURFRPM/i, /^SPP/i].map((p, i) => {
          const c = find(p);
          return c && trackCurve(c, PALETTE[[0, 1, 2, 4][i]]);
        }),
      );
    case 'mud-weight': {
      const mw = find(/^DIN/i, /^MW/i, /mud\s*weight/i);
      return track('Mud Weight', 120, [mw && { ...trackCurve(mw, '#1565c0'), min: 8, max: 20, log: false }]);
    }
    case 'gas': {
      const gas = find(/^TGAS/i);
      return track('Total Gas', TRACK_WIDTHS.narrow, [gas && trackCurve(gas, '#d32f2f')]);
    }
    case 'chromatograph':
      return track(
        'Chromatograph',
        TRACK_WIDTHS.wide,
        [/^C1_/i, /^C2_/i, /^C3_/i, /^IC4_/i, /^NC4_/i, /^IC5_/i, /^NC5_/i]
          .map((p) => find(p))
          .filter((c): c is Curve => Boolean(c))
          .map((c, i) => ({
            ...trackCurve(c, CHROMATOGRAPH_COLOURS[i % CHROMATOGRAPH_COLOURS.length]),
            dash: CHROMATOGRAPH_DASHES[i % CHROMATOGRAPH_DASHES.length],
          })),
      );
  }
}

/** The first layout for a well, each track only when the well has a curve of that kind. */
export function defaultLayout(curves: Curve[], hasLithology: boolean, preset: Preset = 'viewer'): Track[] {
  // The printed logs keep every column; the free viewer only what has data.
  const keepEmpty = preset !== 'viewer';
  const tracks = ORDER[preset]
    .map((p): Track | null =>
      typeof p === 'string'
        ? part(p, curves, hasLithology, keepEmpty)
        : { id: newId(), kind: 'curves', width: p.width, title: p.blank, curves: [] },
    )
    .filter((t): t is Track => t !== null);

  // A well with none of the above still gets one track to start from.
  if (tracks.length === 1 && curves[0]) {
    tracks.push({
      id: newId(),
      kind: 'curves',
      width: TRACK_WIDTHS.normal,
      title: curves[0].mnemonic,
      curves: [trackCurve(curves[0], PALETTE[0])],
    });
  }

  return tracks;
}

/**
 * Where one person's own arrangement is kept in this browser, per well. The
 * scope names the screen (a tab, or the viewer in Documents); the version
 * moves when the starting tracks change, so an arrangement saved in an older
 * order does not hide the new one.
 */
const storageKey = (scope: string, wellId: number) =>
  scope === 'viewer' ? `wxp.logview.${wellId}` : `wxp.log.v6.${scope}.${wellId}`;

export interface SavedLayout {
  tracks: Track[];
  scale: number;
  /** Absent from layouts saved before the log could be turned over. */
  flipped?: boolean;
}

/** A personal arrangement, and the role default it was made from. */
export interface PersonalLayout extends SavedLayout {
  /** signature() of the role default at the time; absent when there was none. */
  base?: string;
}

/**
 * A short fingerprint of a role default. A personal arrangement made from an
 * older default is set aside, so what the superadmin saves reaches everyone.
 */
export function signature(layout: SavedLayout | null | undefined): string | undefined {
  if (!layout) return undefined;
  const text = JSON.stringify(layout);
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/** A role default from a tab's config, when it holds something drawable. */
export function asSavedLayout(raw: { tracks: unknown[]; scale: number; flipped?: boolean } | undefined) {
  if (!raw || !Array.isArray(raw.tracks) || raw.tracks.length === 0) return null;
  return { tracks: raw.tracks as Track[], scale: raw.scale ?? 1, flipped: raw.flipped } satisfies SavedLayout;
}

export function loadLayout(scope: string, wellId: number): PersonalLayout | null {
  try {
    const raw = localStorage.getItem(storageKey(scope, wellId));
    if (!raw) return null;
    const saved = JSON.parse(raw) as Partial<PersonalLayout>;
    if (!Array.isArray(saved.tracks) || saved.tracks.length === 0) return null;
    return { tracks: saved.tracks, scale: saved.scale ?? 1, flipped: saved.flipped, base: saved.base };
  } catch {
    return null;
  }
}

export function saveLayout(scope: string, wellId: number, layout: PersonalLayout) {
  try {
    localStorage.setItem(storageKey(scope, wellId), JSON.stringify(layout));
  } catch {
    /* storage unavailable — the layout lasts as long as the page */
  }
}

export function clearLayout(scope: string, wellId: number) {
  try {
    localStorage.removeItem(storageKey(scope, wellId));
  } catch {
    /* nothing to clear */
  }
}

/**
 * Points a saved layout's curves at this well's files. A layout names curves
 * as "file::mnemonic", and a default saved on one well has to work on wells
 * whose files are called something else: a curve not found by its key is
 * looked for by mnemonic. Layouts from before curves had a drawing style get
 * one too: survey curves draw as stations, everything else as a line.
 */
export function resolveTracks(tracks: Track[], curves: Curve[]): Track[] {
  const byKey = new Map(curves.map((c) => [c.key, c]));
  const byMnemonic = new Map<string, Curve>();
  for (const c of curves) if (!byMnemonic.has(c.mnemonic.toLowerCase())) byMnemonic.set(c.mnemonic.toLowerCase(), c);

  const find = (key: string) => byKey.get(key) ?? byMnemonic.get((key.split('::').pop() ?? key).toLowerCase());

  return tracks.map((t) => {
    if (t.kind === 'depth' && t.curve) {
      const c = find(t.curve);
      return c ? { ...t, curve: c.key } : t;
    }
    if (t.kind !== 'curves') return t;
    return {
      ...t,
      curves: t.curves.map((tc) => {
        const c = find(tc.key);
        const style = tc.style ?? (c && isStationCurve(c) ? 'stations' : 'line');
        return c ? { ...tc, key: c.key, style } : { ...tc, style };
      }),
    };
  });
}
